import React from 'react'

import reactStringReplace from 'react-string-replace'
import Dialog, {
  DialogBody,
  DialogContent,
  DialogFooter,
  FooterActionButton,
  FooterActions,
} from '../Dialog'
import useDialog from '../../hooks/dialog/useDialog'
import useOpenLinkSafely from '../../hooks/useOpenLinkSafely'
import useProcessQr from '../../hooks/useProcessQr'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import { isInviteLink } from '@deltachat-desktop/shared/util'
import { selectedAccountId } from '../../ScreenController'
import { Avatar } from '../Avatar'
import { parseInviteLink } from '../../utils/inviteLink'
import { colorFromString } from '../../utils/colorFromString'

import type { DialogProps } from '../../contexts/DialogContext'
import { SCAN_CONTEXT_TYPE } from '../../hooks/useProcessQr'

type PunycodeWarning = {
  original_hostname_or_full_url: string
  ascii_hostname: string
  punycode_encoded_url: string
}
type LinkDestination = {
  target: string
  hostname: null | string
  punycode: null | PunycodeWarning
  scheme: null | string
  linkText?: string
}

export const Link = ({
  destination,
  tabIndex,
}: {
  destination: LinkDestination
  tabIndex: -1 | 0
}) => {
  const { openDialog } = useDialog()
  const openLinkSafely = useOpenLinkSafely()
  const tx = useTranslationFunction()
  const accountId = selectedAccountId()
  const { target, punycode, scheme, linkText } = destination

  const processQr = useProcessQr()
  const asciiUrl = punycode ? punycode.punycode_encoded_url : target

  const onClick = (ev: any) => {
    ev.preventDefault()
    ev.stopPropagation()

    if (isInviteLink(target)) {
      processQr(accountId, target, SCAN_CONTEXT_TYPE.DEFAULT)
      return
    }

    if (punycode) {
      openDialog(PunycodeUrlConfirmationDialog, {
        originalHostnameOrFullUrl: punycode.original_hostname_or_full_url,
        asciiHostname: punycode.ascii_hostname,
        asciiUrl: scheme
          ? punycode.punycode_encoded_url
          : `https://${punycode.punycode_encoded_url}`,
      })
    } else {
      openLinkSafely(accountId, scheme ? target : `https://${target}`)
    }
  }

  // Invite links render as a card with the name parsed from the URL fragment
  // (never via core's checkQr, which would create a hidden contact just for
  // rendering). The name is sender-controlled and cosmetic; the real decision
  // point stays the confirmation dialog that clicking opens.
  const invite = isInviteLink(target) ? parseInviteLink(target) : null
  if (invite) {
    const label = {
      group: tx('invite_link_group'),
      channel: tx('invite_link_channel'),
      contact: tx('invite_link_contact'),
    }[invite.kind]
    return (
      <a
        href={asciiUrl}
        x-target-url={asciiUrl}
        title={asciiUrl}
        onClick={onClick}
        tabIndex={tabIndex}
        className='invite-link-card'
      >
        {/* Selection-copy must carry the real link, not the card's visible
            words: this span is in the selection but visually imperceptible,
            while the visible part is user-select:none (native copy) and
            data-no-copy (stripped by getSelectedText for the context menu). */}
        {/* aria-hidden: the clip pattern would otherwise read the whole
            fingerprint URL to screen readers; the visible text is the name */}
        <span className='invite-link-url' aria-hidden='true'>
          {target}
        </span>
        <span className='invite-link-visible' data-no-copy>
          <Avatar
            displayName={invite.name}
            color={colorFromString(invite.name)}
            aria-hidden
          />
          <span className='invite-link-body'>
            <span className='invite-link-kind'>{label}</span>
            <span className='invite-link-name'>{invite.name}</span>
          </span>
          <span className='invite-link-view'>{tx('invite_link_view')}</span>
        </span>
      </a>
    )
  }

  return (
    <a
      href={asciiUrl}
      x-target-url={asciiUrl}
      title={asciiUrl}
      onClick={onClick}
      tabIndex={tabIndex}
    >
      {linkText ?? target}
    </a>
  )
}

function PunycodeUrlConfirmationDialog(
  props: {
    originalHostnameOrFullUrl: string
    asciiHostname: string
    asciiUrl: string
  } & DialogProps
) {
  const tx = useTranslationFunction()
  const openLinkSafely = useOpenLinkSafely()
  const accountId = selectedAccountId()

  return (
    <Dialog onClose={props.onClose}>
      <DialogBody>
        <DialogContent>
          <div
            style={{
              fontSize: '1.5em',
              fontWeight: 'lighter',
              marginBottom: '6px',
            }}
          >
            {tx('puny_code_warning_header')}
          </div>
          <p>
            {reactStringReplace(
              tx('puny_code_warning_question', '$$asciiHostname$$'),
              '$$asciiHostname$$',
              () => (
                <b>{props.asciiHostname}</b>
              )
            )}
          </p>
          <hr />
          <p>
            {reactStringReplace(
              reactStringReplace(
                tx('puny_code_warning_description', [
                  '$$originalHostnameOrFullUrl$$',
                  '$$asciiHostname$$',
                ]),
                '$$originalHostnameOrFullUrl$$',
                () => <b>{props.originalHostnameOrFullUrl}</b>
              ),
              '$$asciiHostname$$',
              () => (
                <b>{props.asciiHostname}</b>
              )
            )}
          </p>
        </DialogContent>
      </DialogBody>
      <DialogFooter>
        <FooterActions>
          <FooterActionButton onClick={props.onClose}>
            {tx('no')}
          </FooterActionButton>
          <FooterActionButton
            onClick={() => {
              props.onClose()
              openLinkSafely(accountId, props.asciiUrl)
            }}
          >
            {tx('open')}
          </FooterActionButton>
        </FooterActions>
      </DialogFooter>
    </Dialog>
  )
}
